import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ChevronLeft,
    MoreHorizontal,
    MapPin,
    Phone,
    Mail,
    ChevronDown,
    DollarSign,
    Edit,
    Building,
    User,
} from 'lucide-react';
import { useClientDetails } from '../../../Hooks/useClientDetails';
import { useClientOverview } from '../../../Hooks/useClientOverview';
import ClientOverviewTabs from '../../section/EmptyStateTab';

export default function ClientDetails() {
    const navigate = useNavigate();
    const { client, isLoading: clientLoading, error: clientError } = useClientDetails();
    const { overview, isLoading: overviewLoading, error: overviewError } = useClientOverview();

    const [showMoreActions, setShowMoreActions] = useState(false);
    const [activeTab, setActiveTab] = useState('active-work');

    const tabs = [
        { id: 'active-work', label: 'Active Work' },
        { id: 'requests', label: 'Requests' },
        { id: 'quotes', label: 'Quotes' },
        { id: 'jobs', label: 'Jobs' },
        { id: 'invoices', label: 'Invoices' },
    ];

    if (clientLoading || overviewLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-gray-500">Loading client details...</div>
            </div>
        );
    }

    if (clientError || overviewError || !client) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-red-600">Failed to load client. Please try again.</div>
            </div>
        );
    }

    return (
        <section className="min-h-screen text-sm">
            <div className="bg-white border-b border-gray-200 px-4 py-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">{client.fullName}</h1>
                            <p className="text-xs text-gray-600">Client since {client.clientSince}</p>
                        </div>
                    </div>

                </div>
            </div>

            <div className="px-4 py-5">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    <div className="lg:col-span-2 space-y-5">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                            <h2 className="text-lg font-bold mb-3">Additional Contacts</h2>
                            <div className="text-center py-8 text-gray-500 text-xs">
                                No additional contacts added
                            </div>
                        </div>

                        <div className="bg-white border-b border-gray-200">
                            <div className="flex gap-1 overflow-x-auto px-4">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`relative px-4 py-3 text-xs font-medium whitespace-nowrap transition-colors ${activeTab === tab.id
                                            ? 'text-blue-600'
                                            : 'text-gray-600 hover:text-gray-900'
                                            }`}
                                    >
                                        {tab.label}
                                        {activeTab === tab.id && (
                                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <ClientOverviewTabs activeTab={activeTab} overview={overview} />
                    </div>

                    <div className="space-y-4">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                            <h3 className="font-bold text-sm mb-4">Contact Information</h3>
                            <div className="space-y-4 text-xs">
                                <div className="flex items-center gap-3">
                                    <User className="w-4 h-4 text-gray-500" />
                                    <div>
                                        <p className="text-gray-600">Full Name</p>
                                        <p className="font-medium">{client.fullName}</p>
                                    </div>
                                </div>

                                {client.companyName && (
                                    <div className="flex items-center gap-3">
                                        <Building className="w-4 h-4 text-gray-500" />
                                        <div>
                                            <p className="text-gray-600">Company</p>
                                            <p className="font-medium">{client.companyName}</p>
                                        </div>
                                    </div>
                                )}

                                {client.email && (
                                    <div className="flex items-center gap-3">
                                        <Mail className="w-4 h-4 text-gray-500" />
                                        <div>
                                            <p className="text-gray-600">Email</p>
                                            <p className="font-medium">{client.email}</p>
                                        </div>
                                    </div>
                                )}

                                {client.phone && (
                                    <div className="flex items-center gap-3">
                                        <Phone className="w-4 h-4 text-gray-500" />
                                        <div>
                                            <p className="text-gray-600">Phone</p>
                                            <p className="font-medium">{client.phone}</p>
                                        </div>
                                    </div>
                                )}

                                <div className="pt-2 border-t">
                                    <p className="text-gray-600 mb-1">Client Type</p>
                                    <span
                                        className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${client.tag === 'Corporate'
                                            ? 'bg-purple-100 text-purple-700'
                                            : 'bg-gray-100 text-gray-700'
                                            }`}
                                    >
                                        {client.tag}
                                    </span>
                                </div>

                                <div className="pt-2 border-t">
                                    <p className="text-gray-600 mb-1">Status</p>
                                    <span
                                        className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${client.status === 'Active'
                                            ? 'bg-emerald-100 text-emerald-700'
                                            : 'bg-yellow-100 text-yellow-700'
                                            }`}
                                    >
                                        {client.status}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {client.fullAddress && client.fullAddress !== 'No address provided' && (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                                <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
                                    <MapPin className="w-4 h-4" />
                                    Address
                                </h3>
                                <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-line">
                                    {client.street1}
                                    {client.street2 && `\n${client.street2}`}
                                    {(client.street1 || client.street2) && '\n'}
                                    {client.city}
                                    {client.state ? `, ${client.state}` : ''} {client.postalCode}
                                    {client.country && `\n${client.country}`}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}